import React, { useState, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import { Badge, Modal } from "react-bootstrap";

import { notifyError } from "jsx/components/CitoPlus/components/CitoPlusToastr";
import VigilanteZonasSocialesService from "../../../../../services/VigilanteZonasSocialesService";
import ReporteAveriaModal from "./ReporteAveriaModal";

const CalendarioReservasVigilante = () => {
  const calendarRef = useRef(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showReporteModal, setShowReporteModal] = useState(false);
  const [reportes, setReportes] = useState([]);
  const [loadingReportes, setLoadingReportes] = useState(false);

  const fetchEvents = async (fetchInfo, successCallback, failureCallback) => {
    try {
      const response = await VigilanteZonasSocialesService.getCalendario(
        fetchInfo.startStr,
        fetchInfo.endStr,
      );
      successCallback(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
      notifyError("Error al cargar el calendario de reservas");
      failureCallback(error);
    }
  };

  const handleEventClick = async (clickInfo) => {
    const eventData = {
      ...clickInfo.event.extendedProps,
      title: clickInfo.event.title,
      start: clickInfo.event.start,
      end: clickInfo.event.end,
    };
    setSelectedEvent(eventData);
    setShowModal(true);

    // Fetch reportes para esta reserva
    if (eventData.uuid) {
      await fetchReportes(eventData.uuid);
    }
  };

  const fetchReportes = async (reservaUuid) => {
    setLoadingReportes(true);
    try {
      const response = await VigilanteZonasSocialesService.getReportesAverias(
        reservaUuid,
      );
      setReportes(response.data.results || response.data);
    } catch (error) {
      console.error("Error fetching reportes:", error);
      setReportes([]);
    } finally {
      setLoadingReportes(false);
    }
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      confirmada: <Badge bg="success">Confirmada</Badge>,
      pendiente: <Badge bg="warning">Pendiente</Badge>,
      cancelada: <Badge bg="danger">Cancelada</Badge>,
      incumplida: <Badge bg="secondary">Incumplida</Badge>,
    };
    return estados[estado] || <Badge bg="secondary">{estado}</Badge>;
  };

  const formatDateTime = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return new Intl.DateTimeFormat("es-CO", {
      dateStyle: "full",
      timeStyle: "short",
    }).format(date);
  };

  return (
    <>
      <div className="card">
        <div className="card-header">
          <h4 className="card-title mb-0">Calendario de Reservas</h4>
        </div>
        <div className="card-body">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay",
            }}
            locale={esLocale}
            events={fetchEvents}
            eventClick={handleEventClick}
            height="auto"
            eventDisplay="block"
            eventTimeFormat={{
              hour: "2-digit",
              minute: "2-digit",
              meridiem: false,
            }}
          />
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detalle de Reserva</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEvent && (
            <div className="vstack gap-4">
              <section>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <h5 className="mb-0">Información General</h5>
                  {getEstadoBadge(selectedEvent.estado)}
                </div>
                <hr />
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Título</label>
                    <div className="form-control-plaintext">
                      {selectedEvent.title || "Sin título"}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Zona Social</label>
                    <div className="form-control-plaintext">
                      {selectedEvent.zona_nombre || "No disponible"}
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Descripción</label>
                    <div className="form-control-plaintext">
                      {selectedEvent.descripcion || "Sin descripción"}
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h5>Responsable y Reglas</h5>
                <hr />
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Responsable</label>
                    <div className="form-control-plaintext">
                      {selectedEvent.zona_responsable || "No disponible"}
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Reglas de Uso</label>
                    <div
                      className="form-control-plaintext"
                      dangerouslySetInnerHTML={{
                        __html:
                          selectedEvent.zona_reglas_uso ||
                          "Sin reglas definidas",
                      }}
                    />
                  </div>
                </div>
              </section>

              <section>
                <h5>Horario</h5>
                <hr />
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Inicio</label>
                    <div className="form-control-plaintext">
                      {formatDateTime(selectedEvent.start)}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Fin</label>
                    <div className="form-control-plaintext">
                      {formatDateTime(selectedEvent.end)}
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h5>Residente e Inmueble</h5>
                <hr />
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Residente</label>
                    <div className="form-control-plaintext">
                      {selectedEvent.residente_nombre || "No disponible"}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Inmueble</label>
                    <div className="form-control-plaintext">
                      {selectedEvent.inmueble || "No disponible"}
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h5>Reportes de Averías/Faltantes</h5>
                <hr />
                {loadingReportes ? (
                  <div className="text-center">
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                    >
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                    <span className="ms-2">Cargando reportes...</span>
                  </div>
                ) : reportes.length > 0 ? (
                  <div className="basic-list-group">
                    <div className="list-group">
                      {reportes.map((reporte, index) => (
                        <a
                          key={reporte.uuid}
                          href="#!"
                          className={`list-group-item list-group-item-action flex-column align-items-start active`}
                        >
                          <div className="d-flex w-100 justify-content-between">
                            <h5 className={`mb-3 text-white`}>
                              <Badge bg="danger">{reporte.tipo_display}</Badge>
                            </h5>
                            <small className={`text-white`}>
                              {reporte.created_at}
                            </small>
                          </div>
                          {reporte.detalle && (
                            <p className={`mb-1`}>{reporte.detalle}</p>
                          )}
                          <small className={"text-white"}>
                            <span className={"text-black"}>Reportado por:</span>{" "}
                            {reporte.creado_por_nombre}
                          </small>
                        </a>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-muted text-center">
                    <i className="fas fa-info-circle me-2"></i>
                    No hay reportes de averías/faltantes para esta reserva
                  </div>
                )}
              </section>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-warning me-2"
            onClick={() => setShowReporteModal(true)}
          >
            <i className="fas fa-exclamation-triangle me-1"></i>
            Reportar Avería/Faltante
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowModal(false)}
          >
            Cerrar
          </button>
        </Modal.Footer>
      </Modal>

      <ReporteAveriaModal
        reservaUuid={selectedEvent?.uuid}
        show={showReporteModal}
        onHide={() => setShowReporteModal(false)}
        onSuccess={() =>
          selectedEvent?.uuid && fetchReportes(selectedEvent.uuid)
        }
      />
    </>
  );
};

export default CalendarioReservasVigilante;
